import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Card, HeaderTitle, Table, CardsSVG, CardBody } from '../../../components';
import { FilterQueryParams, downloadReport } from '../../../helpers';
import productsColumns from './ProductsColumns';
import { useProductsUIContext } from '../context/ProductsUIContext';
import { UIHelpers } from '../../../constants/pages/UIHelpers';
import { usersService } from '../../../services';
import ProductFilters from './ProductsFilters';
import page from '../../../constants/pages/page';
import { useTranslation } from 'react-i18next';
import { palette } from '../../../styles/theme';

export default function ProductsTable () {
    const { t } = useTranslation();
    const clientsDropFiltersRef = useRef();
    const customSortedKeyMap = { 'status.description': 'id_creditcard_status' };
    const { sortedKeyMap, defaultSorted } = UIHelpers(customSortedKeyMap);
    // State
    const [totalProducts, setTotalProducts] = useState(0);

    // Context State
    const productsUIContext = useProductsUIContext();
    const productsUIProps = useMemo(() => {
        return {
            isLoading: productsUIContext.isLoading,
            setIsLoading: productsUIContext.setIsLoading,
            queryParams: productsUIContext.queryParams,
            setQueryParams: productsUIContext.setQueryParams,
            cards: productsUIContext.cards,
            setCards: productsUIContext.setCards,
        };
    }, [productsUIContext]);

    // Functions

    const fetchCards = async () => {
        productsUIProps.setIsLoading(true);
        // Prepare options (order, pages and filters)
        const filter = productsUIProps.queryParams.filters || new FilterQueryParams();
        const { sortField, sortOrder } = productsUIProps.queryParams;
        const options = {
            order: `${sortedKeyMap[sortField] || sortField}-${sortOrder.toUpperCase()}`,
            pageSize: productsUIProps.queryParams.pageSize,
            page: productsUIProps.queryParams.pageNumber,
            view: 'clients'
        };

        // Set filters on options.
        options.filters = filter.build();

        // let idUsers = [];

        await usersService
            .getUsersCreditCards(options)
            .then(cards => {
                cards.forEach(card => {
                    if (card.last_four.length < 4) {
                        card.last_four = card.last_four.padStart(4, '0');
                    }
                    if (!card.id_external_account) card.id_external_account = '-';
                    if (!card.id_internal_account) card.id_internal_account = '-';
                });
                productsUIProps.setCards(cards);
            })
            .catch(e => {
                console.error(e);
            })
            .finally(() => productsUIProps.setIsLoading(false));
    };

    const getTotalProducts = async () => {
        productsUIProps.setIsLoading(true);
        const filters = productsUIProps.queryParams.filters || new FilterQueryParams();
        const options = {
            unionPeople: true,
            pageSize: 100000,
            view: 'clients'
        };
        options.filters = filters.build();
        await usersService.getUsersCreditCards(options)
            .then(products => {
                setTotalProducts(products.length);
            })
            .catch(e => {
                console.error(e);
            })
            .finally(() => productsUIProps.setIsLoading(false));
    };

    const getData = async () => {
        await getTotalProducts();
        fetchCards();
    };

    const resetFilters = () => {
        if (clientsDropFiltersRef.current) {
            clientsDropFiltersRef.current.reset();
        }
    };

    const prepareData = () => {
        return productsUIProps.cards;
    };

    const download = () => {
        downloadReport(productsUIContext);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => productsUIProps.setQueryParams(prev => ({ ...prev, pageSize: localStorage.getItem('sizePerPage') || page.pageSize })), []);
    useEffect(() => {
        if (JSON.stringify(productsUIProps.queryParams.url) !== undefined) {
            getData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productsUIProps.queryParams]);
    // Main Component
    return (
        <Card>
            <HeaderTitle
                title={t('cards.table.title')}
                total={totalProducts}
                reset={resetFilters}
                icon={(<CardsSVG customStroke={palette.primary[700]}/>)}
                download={download}
                UIProps={productsUIProps}
                margBottom={'6px'}
            />
            <ProductFilters ref={clientsDropFiltersRef}/>
            <CardBody>
                <Table
                    data={prepareData()}
                    columns={productsColumns()}
                    sort={defaultSorted}
                    onChange={productsUIProps.setQueryParams}
                    isLoading={productsUIProps.isLoading}
                    total={totalProducts}
                    pagination={{
                        sizePerPage: productsUIProps.queryParams.pageSize,
                        page: productsUIProps.queryParams.pageNumber
                    }}
                />
            </CardBody>
        </Card>
    );
}
